import {
	type Announcements,
	DndContext,
	type DragEndEvent,
	type DragMoveEvent,
	type DragOverEvent,
	DragOverlay,
	type DragStartEvent,
	type DropAnimation,
	KeyboardSensor,
	MeasuringStrategy,
	PointerSensor,
	type UniqueIdentifier,
	closestCenter,
	defaultDropAnimation,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { PageMetadata } from "@moment/api-collab/api-types";

import { SortableTreeItem } from "./TreeItem";
import { sortableTreeKeyboardCoordinates } from "./keyboardCoordinates";
import type { FlattenedItem, SensorContext, TreeItems } from "./types";
import {
	buildTree,
	flattenTree,
	getChildCount,
	getProjection,
	removeChildrenOf,
} from "./utilities";

const measuring = {
	droppable: {
		strategy: MeasuringStrategy.Always,
	},
};

const dropAnimationConfig: DropAnimation = {
	keyframes({ transform }) {
		return [
			{ opacity: 1, transform: CSS.Transform.toString(transform.initial) },
			{
				opacity: 0,
				transform: CSS.Transform.toString({
					...transform.final,
					// x: transform.final.x + 5,
					y: transform.final.y + 5,
				}),
			},
		];
	},
	easing: "ease-out",
	sideEffects({ active }) {
		active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
			duration: defaultDropAnimation.duration,
			easing: defaultDropAnimation.easing,
		});
	},
};

interface Props {
	collapsible?: boolean;
	items: TreeItems<PageMetadata>;
	setItemsOrder: (items: TreeItems<PageMetadata>) => void;
	collapseItem: (id: UniqueIdentifier) => void;
	canvasId: string;
	allowPageDelete: (page: PageMetadata) => boolean;
	indentationWidth?: number;
	indicator?: boolean;
	removable?: boolean;
}

export function PageTree({
	collapsible,
	items,
	setItemsOrder,
	collapseItem,
	canvasId,
	allowPageDelete,
	indicator = false,
	indentationWidth = 20,
}: Props) {
	const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
	const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
	const [offsetLeft, setOffsetLeft] = useState(0);
	const [currentPosition, setCurrentPosition] = useState<{
		parentId: UniqueIdentifier | null;
		overId: UniqueIdentifier;
	} | null>(null);

	const flattenedItems = useMemo(() => {
		const flattenedTree = flattenTree(items);
		const collapsedItems = flattenedTree.reduce<(string | number)[]>(
			(acc, { children, collapsed, id }) =>
				collapsed && children.length ? [...acc, id] : acc,
			[]
		);

		return removeChildrenOf(
			flattenedTree,
			activeId ? [activeId, ...collapsedItems] : collapsedItems
		);
	}, [activeId, items]);

	const projected =
		activeId && overId
			? getProjection(flattenedItems, activeId, overId, offsetLeft, indentationWidth)
			: null;
	const sensorContext: SensorContext<PageMetadata> = useRef({
		items: flattenedItems,
		offset: offsetLeft,
	});
	const [coordinateGetter] = useState(() =>
		sortableTreeKeyboardCoordinates(sensorContext, indicator, indentationWidth)
	);
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { delay: 300, tolerance: Number.MAX_SAFE_INTEGER },
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter,
		})
	);

	const sortedIds = useMemo(() => flattenedItems.map(({ id }) => id), [flattenedItems]);
	const activeItem = activeId ? flattenedItems.find(({ id }) => id === activeId) : null;

	useEffect(() => {
		sensorContext.current = {
			items: flattenedItems,
			offset: offsetLeft,
		};
	}, [flattenedItems, offsetLeft]);

	const announcements: Announcements = {
		onDragStart({ active }) {
			return `Picked up ${active.id}.`;
		},
		onDragMove({ active, over }) {
			return getMovementAnnouncement("onDragMove", active.id, over?.id);
		},
		onDragOver({ active, over }) {
			return getMovementAnnouncement("onDragOver", active.id, over?.id);
		},
		onDragEnd({ active, over }) {
			return getMovementAnnouncement("onDragEnd", active.id, over?.id);
		},
		onDragCancel({ active }) {
			return `Moving was cancelled. ${active.id} was dropped in its original position.`;
		},
	};

	return (
		<DndContext
			accessibility={{ announcements }}
			sensors={sensors}
			collisionDetection={closestCenter}
			measuring={measuring}
			onDragStart={handleDragStart}
			onDragMove={handleDragMove}
			onDragOver={handleDragOver}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}
		>
			<SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
				{flattenedItems.map((item) => {
					const { id, children, collapsed, depth, data } = item;
					return (
						<SortableTreeItem
							key={id}
							id={id}
							item={item}
							canvasId={canvasId}
							allowDelete={allowPageDelete(data)}
							depth={id === activeId && projected ? projected.depth : depth}
							childCount={getChildCount(items, id) + 1}
							indentationWidth={indentationWidth}
							collapsed={Boolean(collapsed && children.length)}
							onCollapse={
								collapsible && children.length ? () => collapseItem(id) : undefined
							}
						/>
					);
				})}
				{
					// Overlay "snaps" into place as we drag around navigation pane. If we omit
					// this, Dnd does not work.
					createPortal(<DragOverlay dropAnimation={dropAnimationConfig} />, document.body)
				}
			</SortableContext>
		</DndContext>
	);

	function handleDragStart({ active: { id: activeId } }: DragStartEvent) {
		setActiveId(activeId);
		setOverId(activeId);

		const activeItem = flattenedItems.find(({ id }) => id === activeId);

		if (activeItem) {
			setCurrentPosition({
				parentId: activeItem.parentId,
				overId: activeId,
			});
		}

		document.body.style.setProperty("cursor", "grabbing");
	}

	function handleDragMove({ delta }: DragMoveEvent) {
		setOffsetLeft(delta.x);
	}

	function handleDragOver({ over }: DragOverEvent) {
		setOverId(over?.id ?? null);
	}

	function handleDragEnd({ active, over }: DragEndEvent) {
		resetState();

		if (projected && over) {
			const { depth, parentId } = projected;
			const clonedItems: FlattenedItem<PageMetadata>[] = JSON.parse(
				JSON.stringify(flattenTree(items))
			);
			const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
			const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
			const activeTreeItem = clonedItems[activeIndex];

			clonedItems[activeIndex] = {
				...activeTreeItem,
				depth,
				parentId,
				data: { ...activeItem?.data, parentPageID: parentId },
			} as FlattenedItem<PageMetadata>;

			const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
			const newItems = buildTree(sortedItems);

			setItemsOrder(newItems);
		}
	}

	function handleDragCancel() {
		resetState();
	}

	function resetState() {
		setOverId(null);
		setActiveId(null);
		setOffsetLeft(0);
		setCurrentPosition(null);

		document.body.style.setProperty("cursor", "");
	}

	function getMovementAnnouncement(
		eventName: string,
		activeId: UniqueIdentifier,
		overId?: UniqueIdentifier
	) {
		if (overId && projected) {
			if (eventName !== "onDragEnd") {
				if (
					currentPosition &&
					projected.parentId === currentPosition.parentId &&
					overId === currentPosition.overId
				) {
					return;
				} else {
					setCurrentPosition({
						parentId: projected.parentId,
						overId,
					});
				}
			}

			const clonedItems: FlattenedItem<PageMetadata>[] = JSON.parse(
				JSON.stringify(flattenTree(items))
			);
			const overIndex = clonedItems.findIndex(({ id }) => id === overId);
			const activeIndex = clonedItems.findIndex(({ id }) => id === activeId);
			const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);

			const previousItem = sortedItems[overIndex - 1];

			let announcement: string | undefined;
			const movedVerb = eventName === "onDragEnd" ? "dropped" : "moved";
			const nestedVerb = eventName === "onDragEnd" ? "dropped" : "nested";

			if (!previousItem) {
				const nextItem = sortedItems[overIndex + 1];
				announcement = `${activeId} was ${movedVerb} before ${nextItem?.id}.`;
			} else {
				if (projected.depth > previousItem.depth) {
					announcement = `${activeId} was ${nestedVerb} under ${previousItem.id}.`;
				} else {
					let previousSibling: FlattenedItem<PageMetadata> | undefined = previousItem;
					while (previousSibling && projected.depth < previousSibling.depth) {
						const parentId: UniqueIdentifier | null = previousSibling.parentId;
						previousSibling = sortedItems.find(({ id }) => id === parentId);
					}

					if (previousSibling) {
						announcement = `${activeId} was ${movedVerb} after ${previousSibling.id}.`;
					}
				}
			}

			return announcement;
		}

		return;
	}
}
